import React, { ReactNode, createContext, useContext } from 'react'
import { createIntl, createIntlCache, RawIntlProvider, ReactIntlErrorCode } from 'react-intl'
import messages from './messages'
import { normalizeLanguage } from './helpers'
import { DEFAULT_LOCALE, locales, Locales } from './locales'

type LocaleContextProps = {
  setLocale: (newLocale: string) => void
}

type Props = {
  children: ReactNode
  locale?: Locales
}
const LocaleContext = createContext<LocaleContextProps | undefined>(undefined)

export const cache = createIntlCache()

type IntlMessages = Parameters<typeof createIntl>[0]['messages']

const getIntl = (locale: string, newMessages?: IntlMessages) => {
  const intlMessages = newMessages || {
    ...messages[locale as keyof typeof messages].concierge,
    ...(messages[locale as keyof typeof messages]?.chat ?? {}),
  }
  return createIntl(
    {
      locale: locale,
      messages: intlMessages,
      onError: error => {
        if (error.code === ReactIntlErrorCode.MISSING_TRANSLATION) {
          return
        }
      },
    },
    cache
  )
}

/** You can use this variable in other files even after reassigning it. */
export let intl = getIntl(DEFAULT_LOCALE)
export let fmt = intl.formatMessage

export const LocaleProvider = ({ children, locale = DEFAULT_LOCALE }: Props) => {
  const setLocale = (newLocale: string) => {
    const normalizedLocale = normalizeLanguage(newLocale, locales)
    const translations = {
      ...messages[normalizedLocale as keyof typeof messages]?.concierge,
      ...messages[normalizedLocale as keyof typeof messages]?.chat,
    }
    if (Object.keys(translations).length > 0) {
      intl = getIntl(newLocale, translations)
      fmt = intl.formatMessage
    }
  }

  setLocale(locale)

  return (
    <LocaleContext.Provider value={{ setLocale }}>
      <RawIntlProvider value={intl}>{children}</RawIntlProvider>
    </LocaleContext.Provider>
  )
}

export const useTranslations = (keys: string[]) => {
  const value = useContext(LocaleContext)
  if (!value) {
    throw new Error('useTranslations must be used inside of a LocalizationProvider')
  }

  return keys.map(key => fmt({ id: key }))
}

export const useLocale = () => {
  const value = useContext(LocaleContext)
  if (!value) {
    throw new Error('useLocale must be used inside of a LocalizationProvider')
  }

  return intl.locale
}

export const useSetLocale = () => {
  const value = useContext(LocaleContext)
  if (!value) {
    throw new Error('useSetLocale must be used inside of a LocalizationProvider')
  }

  return value.setLocale
}
